import type { ReactNode } from "react";
import {
  FileSpreadsheet,
  Layers,
  Map as MapIcon,
  Sprout,
  Truck,
} from "lucide-react";
import {
  useFieldToolsStore,
  type FieldAction,
} from "../stores/fieldToolsStore";
import { useUiStore } from "../stores/uiStore";
import type { DashboardField } from "../types/contract";

// Shared button chrome, so a button in the centre and one in the left menu still look like siblings.
function actionClass(active: boolean, variant: "primary" | "secondary") {
  return variant === "primary"
    ? `flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:opacity-40 ${
        active
          ? "border-accent bg-accent text-canvas ring-2 ring-accent/30"
          : "border-accent bg-accent text-canvas hover:bg-accent-hover"
      }`
    : `flex items-center gap-1.5 rounded px-2.5 py-1.5 text-[12px] disabled:opacity-40 ${
        active
          ? "bg-accent/10 text-fg"
          : "text-muted hover:bg-hover hover:text-fg"
      }`;
}

// Opens the map view for the whole selection. Same filled button as Map Soil and the rest of that row.
export function VisualizeButton({ selected }: { selected: DashboardField[] }) {
  const openMapFields = useUiStore((s) => s.openMapFields);
  return (
    <button
      disabled={selected.length === 0}
      // the map's own header switcher flips focus among the selected fields
      onClick={() =>
        selected.length && openMapFields(selected.map((f) => f.field_id))
      }
      className={actionClass(false, "primary")}
    >
      <MapIcon size={14} /> Visualize
    </button>
  );
}

// Centre of the header: Map Soil, Build Rx, and how many fields are selected.
export function FieldActionCenter({
  selected,
}: {
  selected: DashboardField[];
}) {
  const action = useFieldToolsStore((s) => s.action);
  const toggleAction = useFieldToolsStore((s) => s.toggleAction);
  const n = selected.length;
  const btn = (a: FieldAction, icon: ReactNode, label: string) => (
    <button
      disabled={n === 0}
      onClick={() => toggleAction(a)}
      className={actionClass(action === a, "primary")}
    >
      {icon} {label}
    </button>
  );
  return (
    <>
      <VisualizeButton selected={selected} />
      {btn("soilmap", <Layers size={14} />, "Map Soil")}
      {btn("fert", <Sprout size={14} />, "Build Rx")}
      {/* SPREAD FERTILIZER sits BETWEEN them because that is the order of the work: map the soil, decide what the ground needs, turn that into product, file the deduction. */}
      {btn("spread", <Truck size={14} />, "Spread Fertilizer")}
      {btn("section180", <FileSpreadsheet size={14} />, "File Section 180")}
      <span className="whitespace-nowrap text-[11px] text-muted">
        {n === 0
          ? "Select fields to act on them"
          : `${n} field${n === 1 ? "" : "s"} selected`}
      </span>
    </>
  );
}
